import { getTradeCount, getActiveListings } from "./firebaseService";
import { fetchSubscriptionByUserId } from "./subscriptionService";
import { fetchPlanByStripePriceId } from "./planService";
import { awardFreeTradesAfterVerification } from "./userService";
import { firestore, doc, getDoc, updateDoc, collection, query, where, getDocs } from "../firebase/firebaseConfig";
import { getActiveSubscription, getBillingHistory } from "./firebaseService";


/**
 * Checks if the user can create a new listing or contact a seller based on their subscription.
 * Ensures free trades are only deducted when an actual trade is attempted.
 * 
 * @param {string} userId - The ID of the user.
 * @returns {Promise<{ canProceed: boolean, kycVerified: boolean, usedFreeTrade: boolean, tradesThisCycle: number, tradeLimit: number, listingLimit: number, activeListings: number, freeTrades: number, quotaExceeded: boolean }>}
 */
export const enforceQuotaAndListingLimits = async (userId) => {
  if (!userId) {
    console.warn("No user ID provided.");
    return { canProceed: false, kycVerified: false, usedFreeTrade: false, quotaExceeded: false };
  }

  try {
    // Fetch user profile
    const userRef = doc(firestore, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.warn(`User ${userId} not found.`);
      return { canProceed: false, kycVerified: false, usedFreeTrade: false, quotaExceeded: false };
    }

    const userData = userSnap.data();
    const kycVerified = userData.kycVerified || false;
    let freeTrades = userData.freeTrades || 0;
    let tradesThisCycle = userData.tradesThisCycle || 0;

    // **Enforce KYC verification requirement**
    if (!kycVerified) {
      console.warn(`User ${userId} is not KYC verified.`);
      return { canProceed: false, kycVerified: false, usedFreeTrade: false, quotaExceeded: false };
    }

    await awardFreeTradesAfterVerification(userId);

    // **Fetch active listings count**
    const activeListings = await getActiveListings(userId);
    console.log("Active listings:", activeListings);

    // ✅ Allow free trades without requiring an active subscription
    if (freeTrades > 0) {
      const listingLimit = 1; // ✅ Free trade users can only have 1 active listing
      const quotaExceeded = activeListings >= listingLimit;

      return {
        canProceed: true,
        kycVerified: true,
        usedFreeTrade: true,
        freeTrades,
        tradesThisCycle,
        tradeLimit: "N/A",
        listingLimit,
        activeListings,
        quotaExceeded,
      };
    }

    // Fetch the user's active subscription
    const activeSubscription = await getActiveSubscription(userId);
    console.log("Active subscription:", activeSubscription);
    if (!activeSubscription) {
      console.warn(`No active subscription found for user ${userId}`);
      return {
        canProceed: false,
        kycVerified: true,
        usedFreeTrade: false,
        freeTrades: 0,
        tradesThisCycle,
        tradeLimit: "N/A",
        listingLimit: "N/A",
        activeListings,
        quotaExceeded: true, // ✅ No subscription and no free trades → quota exceeded
      };
    }

    //const { subscriptionId, planId } = subscriptionData;

    // **Check user's plan limits**
    const planData = await fetchPlanByStripePriceId(activeSubscription.planId);
    const matchingCycle = planData.billingCycles.find(
      (cycle) => cycle.stripePriceId === activeSubscription.planId
    );

    if (!matchingCycle) {
      console.error("Matching billing cycle not found for planId:", planId);
      return {
        canProceed: false,
        kycVerified: true,
        usedFreeTrade: false,
        freeTrades: 0,
        tradesThisCycle,
        tradeLimit: "N/A",
        listingLimit: "N/A",
        activeListings,
        quotaExceeded: true, // ✅ Cannot proceed without a valid plan
      };
    }

    const tradeLimit = matchingCycle.tradeLimit;
    console.log("Trade limit:", tradeLimit);
    const listingLimit = matchingCycle.listingLimit;

    // **Check if user has exceeded trade limit**
    if (tradeLimit !== -1 && tradesThisCycle >= tradeLimit) {
      console.warn("User exceeded transaction limit:", tradesThisCycle, "/", tradeLimit);
      return {
        canProceed: false,
        kycVerified: true,
        usedFreeTrade: false,
        freeTrades: 0,
        tradesThisCycle,
        tradeLimit,
        listingLimit,
        activeListings,
        quotaExceeded: true, // ✅ Trade quota exceeded
      };
    }

    // **Check if user has exceeded listing limit**
    if (listingLimit !== "N/A" && activeListings >= listingLimit) {
      console.warn("User exceeded listing limit:", activeListings, "/", listingLimit);
      return {
        canProceed: false,
        kycVerified: true,
        usedFreeTrade: false,
        freeTrades: 0,
        tradesThisCycle,
        tradeLimit,
        listingLimit,
        activeListings,
        quotaExceeded: true, // ✅ Listing quota exceeded
      };
    }

    return {
      canProceed: true,
      kycVerified: true,
      usedFreeTrade: false,
      freeTrades: 0,
      tradesThisCycle,
      tradeLimit,
      listingLimit,
      activeListings,
      quotaExceeded: false, // ✅ User is within limits
    };
  } catch (error) {
    console.error("Error enforcing quota and listing limits:", error);
    return {
      canProceed: false,
      kycVerified: false,
      usedFreeTrade: false,
      freeTrades: 0,
      tradesThisCycle: 0,
      tradeLimit: "N/A",
      listingLimit: "N/A",
      activeListings: 0,
      quotaExceeded: true, // ✅ Safe fallback
    };
  }
};


/**
 * Increments quotaUsed for a given user.
 * @param {string} userId
 */
export const incrementQuotaUsage = async (userId) => {
  const userRef = doc(firestore, "users", userId);
  await updateDoc(userRef, {
    quotaUsed: increment(1),
  });
};

/**
 * Resets quotaUsed for all users.
 */
export const resetQuotaForAllUsers = async () => {
  const usersRef = collection(firestore, "users");
  const snapshot = await getDocs(usersRef);

  const updates = snapshot.docs.map((docSnap) =>
    updateDoc(docSnap.ref, { quotaUsed: 0 })
  );

  await Promise.all(updates);
};
